import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, addDoc, query, where, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { GRADOS, DIMENSIONES, PERIODOS } from '../../constants';

// --- Componente principal para la página de Logros ---
export default function AchievementsPage({ user, currentYear }) {
    // --- ESTADOS PARA EL FORMULARIO DE AGREGAR ---
    const [descripcion, setDescripcion] = useState('');
    const [grado, setGrado] = useState(GRADOS[0]);
    const [dimension, setDimension] = useState(DIMENSIONES[0]);
    const [periodo, setPeriodo] = useState(PERIODOS[0]);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- ESTADOS PARA LA LISTA Y EL MODAL DE EDICIÓN ---
    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentAchievement, setCurrentAchievement] = useState(null);

    // --- EFECTO PARA CARGAR LOS LOGROS EN TIEMPO REAL ---

    // --- NUEVOS ESTADOS DE FILTRO ---
    const [filterGrado, setFilterGrado] = useState('Todos');
    const [filterPeriodo, setFilterPeriodo] = useState('Todos');
    const [filterDimension, setFilterDimension] = useState('Todas');

    // --- LOGICA DE FILTRADO ---
    const filteredAchievements = achievements.filter(ach => {
        const matchGrado = filterGrado === 'Todos' || ach.grado === filterGrado;
        const matchPeriodo = filterPeriodo === 'Todos' || ach.periodo === Number(filterPeriodo);
        const matchDimension = filterDimension === 'Todas' || ach.dimension === filterDimension;
        return matchGrado && matchPeriodo && matchDimension;
    });

    useEffect(() => {
        if (!user || !currentYear) return;
        setLoading(true);
        // Similar to StudentList: fetch all for teacher, filter by year in memory to handle legacy
        const q = query(collection(db, "logros"), where("profesorId", "==", user.uid));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const achievementsList = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(ach => {
                    const achYear = ach.year || 2025;
                    return achYear === currentYear;
                });
            setAchievements(achievementsList);
            setLoading(false);
        }, (err) => {
            console.error("Error al obtener logros:", err);
            setError("No se pudieron cargar los logros.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user, currentYear]);

    // --- FUNCIÓN PARA AGREGAR UN NUEVO LOGRO ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setIsSubmitting(true);
        try {
            await addDoc(collection(db, "logros"), {
                descripcion,
                grado,
                dimension,
                periodo: Number(periodo),
                profesorId: user.uid,
                year: currentYear // Save with current year
            });
            setMessage('¡Logro agregado exitosamente!');
            setDescripcion('');
        } catch (err) {
            setError('Error al agregar el logro: ' + err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- FUNCIÓN PARA ELIMINAR UN LOGRO ---
    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este logro? Esta acción no se puede deshacer.")) {
            try {
                await deleteDoc(doc(db, "logros", id));
                setMessage("Logro eliminado correctamente.");
            } catch (err) {
                setError("Error al eliminar el logro: " + err.message);
            }
        }
    };

    // --- FUNCIONES PARA MANEJAR EL MODAL DE EDICIÓN ---
    const openEditModal = (achievement) => {
        setCurrentAchievement(achievement);
        setIsModalOpen(true);
    };

    const closeEditModal = () => {
        setIsModalOpen(false);
        setCurrentAchievement(null);
    };

    const handleUpdate = async (updatedData) => {
        if (!currentAchievement) return;
        try {
            const achievementRef = doc(db, "logros", currentAchievement.id);
            await updateDoc(achievementRef, updatedData);
            setMessage("Logro actualizado correctamente.");
            closeEditModal();
        } catch (err) {
            setError("Error al actualizar el logro: " + err.message);
        }
    };

    return (
        <div className="space-y-8 animate-fade-in">
            {/* --- FORMULARIO PARA AGREGAR LOGROS --- */}
            <div className="glass-card p-8 rounded-2xl">
                <h2 className="text-2xl font-bold mb-6 text-slate-800 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-secondary-600">
                    Agregar Logro Académico
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-slate-600 font-semibold mb-2">Descripción del Logro</label>
                        <textarea
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            className="glass-input w-full px-4 py-3 rounded-xl min-h-[100px]"
                            placeholder="Describa el logro o indicador de desempeño..."
                            required
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-slate-600 font-semibold mb-2">Grado</label>
                            <select value={grado} onChange={(e) => setGrado(e.target.value)} className="glass-input w-full px-4 py-3 rounded-xl">
                                {GRADOS.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-slate-600 font-semibold mb-2">Dimensión</label>
                            <select value={dimension} onChange={(e) => setDimension(e.target.value)} className="glass-input w-full px-4 py-3 rounded-xl">
                                {DIMENSIONES.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-slate-600 font-semibold mb-2">Período</label>
                            <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="glass-input w-full px-4 py-3 rounded-xl">
                                {PERIODOS.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button type="submit" disabled={isSubmitting} className="btn-primary px-8 py-3 rounded-xl font-bold disabled:opacity-70">
                            {isSubmitting ? 'Agregando...' : 'Agregar Logro'}
                        </button>
                    </div>
                </form>
                {message && <div className="mt-6 p-4 rounded-xl bg-green-50 border border-green-100 text-green-600 font-medium animate-pulse">{message}</div>}
                {error && <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 font-medium animate-pulse">{error}</div>}
            </div>



            {/* --- LISTA DE LOGROS EXISTENTES --- */}
            <div className="glass-card p-8 rounded-2xl">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <h2 className="text-2xl font-bold text-slate-800">Listado de Logros</h2>

                    {/* FILTROS */}
                    <div className="flex flex-wrap gap-4">
                        <select
                            value={filterGrado}
                            onChange={(e) => setFilterGrado(e.target.value)}
                            className="glass-input px-3 py-2 rounded-xl text-sm"
                        >
                            <option value="Todos">Todos los Grados</option>
                            {GRADOS.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>

                        <select
                            value={filterDimension}
                            onChange={(e) => setFilterDimension(e.target.value)}
                            className="glass-input px-3 py-2 rounded-xl text-sm max-w-[200px]"
                        >
                            <option value="Todas">Todas las Dimensiones</option>
                            {DIMENSIONES.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>

                        <select
                            value={filterPeriodo}
                            onChange={(e) => setFilterPeriodo(e.target.value)}
                            className="glass-input px-3 py-2 rounded-xl text-sm"
                        >
                            <option value="Todos">Todos los Períodos</option>
                            {PERIODOS.map(p => <option key={p} value={p}>Período {p}</option>)}
                        </select>
                    </div>
                </div>

                {loading ? <div className="text-center p-8 text-slate-500">Cargando logros...</div> : (
                    <div className="overflow-hidden rounded-xl border border-slate-200/50">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/50 border-b border-slate-200/50 text-slate-500 uppercase text-xs tracking-wider">
                                    <th className="p-4 font-bold w-1/2">Descripción</th>
                                    <th className="p-4 font-bold">Grado</th>
                                    <th className="p-4 font-bold">Dimensión</th>
                                    <th className="p-4 font-bold text-center">Período</th>
                                    <th className="p-4 font-bold text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredAchievements.length > 0 ? (
                                    filteredAchievements.map(ach => (
                                        <tr key={ach.id} className="hover:bg-slate-50/50 transition-colors group">
                                            <td className="p-4 text-slate-700 font-medium text-sm leading-relaxed">{ach.descripcion}</td>
                                            <td className="p-4 text-slate-600 text-sm">
                                                <span className="bg-primary-50 text-primary-700 px-2 py-1 rounded-md">{ach.grado}</span>
                                            </td>
                                            <td className="p-4 text-slate-600 text-sm">{ach.dimension}</td>
                                            <td className="p-4 text-center text-slate-600 text-sm">{ach.periodo}</td>
                                            <td className="p-4 text-center space-x-2">
                                                <button onClick={() => openEditModal(ach)} className="text-primary-600 hover:text-primary-800 font-medium text-sm transition-colors hover:bg-primary-50 px-3 py-1 rounded-lg">Editar</button>
                                                <button onClick={() => handleDelete(ach.id)} className="text-red-500 hover:text-red-700 font-medium text-sm transition-colors hover:bg-red-50 px-3 py-1 rounded-lg">Eliminar</button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-slate-400 italic">No hay logros que coincidan con los filtros.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* --- MODAL DE EDICIÓN --- */}
            {isModalOpen && currentAchievement && (
                <EditModal
                    achievement={currentAchievement}
                    onClose={closeEditModal}
                    onUpdate={handleUpdate}
                />
            )}
        </div>
    );
}

// --- Componente para el Modal de Edición ---
function EditModal({ achievement, onClose, onUpdate }) {
    const [editedData, setEditedData] = useState({ ...achievement });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedData(prev => ({ ...prev, [name]: name === 'periodo' ? Number(value) : value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onUpdate(editedData);
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
            <div className="glass-card w-full max-w-lg p-8 rounded-2xl shadow-2xl animate-slide-up">
                <h2 className="text-2xl font-bold mb-6 text-slate-800">Editar Logro</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-slate-600 font-semibold mb-1">Descripción</label>
                        <textarea name="descripcion" value={editedData.descripcion} onChange={handleChange} className="glass-input w-full px-4 py-2 rounded-xl" rows="4" required />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-slate-600 font-semibold mb-1">Grado</label>
                            <select name="grado" value={editedData.grado} onChange={handleChange} className="glass-input w-full px-3 py-2 rounded-xl text-sm">{GRADOS.map(g => <option key={g} value={g}>{g}</option>)}</select>
                        </div>
                        <div>
                            <label className="block text-slate-600 font-semibold mb-1">Dimensión</label>
                            <select name="dimension" value={editedData.dimension} onChange={handleChange} className="glass-input w-full px-3 py-2 rounded-xl text-sm">{DIMENSIONES.map(d => <option key={d} value={d}>{d}</option>)}</select>
                        </div>
                        <div>
                            <label className="block text-slate-600 font-semibold mb-1">Período</label>
                            <select name="periodo" value={editedData.periodo} onChange={handleChange} className="glass-input w-full px-3 py-2 rounded-xl text-sm">{PERIODOS.map(p => <option key={p} value={p}>{p}</option>)}</select>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3 mt-6">
                        <button type="button" onClick={onClose} className="btn-secondary px-6 py-2 rounded-xl">Cancelar</button>
                        <button type="submit" className="btn-primary px-6 py-2 rounded-xl">Guardar Cambios</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
